import classnames from 'classnames';
import { ButtonRounded, Loader, TextInput } from 'is24-corecss';
import React from 'react';
import { useForm } from 'react-hook-form';
import styles from './styles';
import { isEqual } from '@/utils/isEqual.util';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useGetInsuranceDetails, useUpdateInsuranceDetails } from '@/apiHooks/insurance.hook';
import { InsuranceServiceName, UpdateInsuranceDetails } from '@/@types/insurance';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';

export interface AddressModifyFormProps {
  close: () => void;
  setModificationForm: (modification: boolean) => void;
  isLoading: boolean;
  isEntitled: boolean;
  serviceName: InsuranceServiceName;
}

export const AddressModifyForm: React.FC<AddressModifyFormProps> = ({
  close,
  setModificationForm,
  isLoading,
  isEntitled,
  serviceName,
}) => {
  const { t } = useIntl();
  const { insuranceDetails } = useGetInsuranceDetails(isEntitled);
  const { mutate: mutateUpdateDetails } = useUpdateInsuranceDetails(insuranceDetails!.insuranceNumber);
  const isMeinPlus = useHasMeinPlus();

  const defaultValues = {
    firstName: insuranceDetails?.user?.firstName || '',
    lastName: insuranceDetails?.user?.lastName || '',
    city: insuranceDetails?.address?.city || '',
    houseNumber: insuranceDetails?.address?.houseNumber || '',
    street: insuranceDetails?.address?.street || '',
    postcode: insuranceDetails?.address?.postalCode || '',
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ defaultValues, values: defaultValues, mode: 'all', criteriaMode: 'all' });

  const onSubmit = async (values: any) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const formDetails: UpdateInsuranceDetails = {
        user: {
          firstName: values.firstName,
          lastName: values.lastName,
        },
        address: {
          street: values.street,
          houseNumber: values.houseNumber,
          postalCode: values.postcode,
          city: values.city,
        },
      };

      const isAddressChanged = !isEqual(formDetails.address, insuranceDetails?.address);
      const isUserChanged = !isEqual(formDetails.user, insuranceDetails?.user);
      if (isAddressChanged || isUserChanged) {
        await mutateUpdateDetails({
          insuranceDetails: formDetails,
        });
      }

      setModificationForm(false);
    } catch (e) {
      //todo send error message
    } finally {
      await report(TrackingEvents[serviceName].FUNNEL_MODIFY, isMeinPlus);
      setModificationForm(false);
    }
  };

  return (
    <form className={styles.aragModifyContainer} onSubmit={handleSubmit(onSubmit)} data-testid='AddressModifyForm'>
      <div className={styles.content}>
        <div className={styles.column}>
          {isLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <>
            <div className={styles.row}>
              <TextInput
                id='firstName'
                valid={!errors.firstName}
                label={t({
                  id: 'common.aragModal.form.firstName.label',
                  defaultMessage: 'Vorname',
                })}
                placeholder=''
                {...register('firstName', { required: true })}
                data-testid='firstName'
              />
            </div>
            <div className={styles.row}>
              <TextInput
                id='lastName'
                valid={!errors.lastName}
                label={t({
                  id: 'common.aragModal.form.lastName.label',
                  defaultMessage: 'Nachname',
                })}
                {...register('lastName', { required: true })}
                placeholder=''
                data-testid='lastName'
              />
            </div>
            <div className={classnames(styles.address1, styles.row)}>
              <div className={styles.street}>
                <TextInput
                  id='street'
                  valid={!errors.street}
                  label={t({
                    id: 'common.aragModal.form.street.label',
                    defaultMessage: 'Straße',
                  })}
                  {...register('street', { required: true })}
                  placeholder=''
                  data-testid='street'
                />
              </div>
              <div className={styles.houseNumber}>
                <TextInput
                  id='houseNumber'
                  valid={!errors.houseNumber}
                  label={t({
                    id: 'common.aragModal.form.houseNumber.label',
                    defaultMessage: 'Hausnr.',
                  })}
                  placeholder=''
                  {...register('houseNumber', { required: true })}
                  data-testid='houseNumber'
                />
              </div>
            </div>
            <div className={classnames(styles.row, styles.address2)}>
              <div className={styles.postcode}>
                <TextInput
                  id='postcode'
                  valid={!errors.postcode}
                  label={t({
                    id: 'common.aragModal.form.postcode.label',
                    defaultMessage: 'PLZ',
                  })}
                  placeholder=''
                  {...register('postcode', { required: true, pattern: /^[0-9]{5}$/ })}
                  data-testid='postcode'
                />
              </div>
              <div className={styles.city}>
                <TextInput
                  id='city'
                  valid={!errors.city}
                  label={t({
                    id: 'common.aragModal.form.city.label',
                    defaultMessage: 'Ort',
                  })}
                  placeholder=''
                  {...register('city', { required: true })}
                  data-testid='city'
                />
              </div>
            </div>
          </>
        </div>
      </div>
      <div className={styles.modifyFooter}>
        <ButtonRounded
          label={t({ id: 'common.aragModal.form.cancelButtonText', defaultMessage: 'Abbrechen' })}
          onClick={close}
          type='button'
          variant='textDefault'
          className='margin-right-m button-link'
        />
        <ButtonRounded
          label={t({ id: 'common.aragModal.form.saveButtonText', defaultMessage: 'Speichern' })}
          type='submit'
          disabled={isLoading}
        />
      </div>
    </form>
  );
};
